import { useCallback, useEffect, useState } from "react";
import { ClipboardList, Loader2, Sparkles, AlertTriangle, CheckCircle2, ListChecks, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Layout } from "../components/Layout";
import { RiskBadge, DocTypeBadge } from "../components/RiskBadge";

const SEVERITY_CLS = {
  low: "bg-[#E6F0EB] border-[#34745C]/20 text-[#34745C]",
  medium: "bg-[#FDF3E1] border-[#C48231]/20 text-[#C48231]",
  high: "bg-[#FCEAE8] border-[#B54D40]/20 text-[#B54D40]",
};

export default function CarePlanPage() {
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [reports, setReports] = useState([]);
  const [current, setCurrent] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    const [docsRes, repRes] = await Promise.all([
      api.get("/documents", { params: { limit: 100 } }),
      api.get("/careplan/reports"),
    ]);
    setDocs(docsRes.data.items.filter((d) => d.status === "analyzed"));
    setReports(repRes.data);
    setCurrent((cur) => cur || repRes.data[0] || null);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < 10 ? [...s, id] : s));

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/careplan/analyze", { document_ids: selected });
      setCurrent(data);
      setSelected([]);
      toast.success("Care plan analysis complete");
      api.get("/careplan/reports").then((r) => setReports(r.data));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Analysis failed");
    } finally {
      setRunning(false);
    }
  };

  const r = current?.report;

  return (
    <Layout>
      <div data-testid="careplan-page" className="space-y-8">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Care Plan Analyzer</p>
          <h1 className="mt-2 text-3xl font-heading font-light tracking-tight">
            Compare plan vs. <span className="font-bold text-[#2E5C50]">reality</span>
          </h1>
          <p className="mt-2 text-[#57605A] max-w-2xl">
            Select a care plan plus related documents (physician orders, notes, timesheets) and Nexari will find
            inconsistencies, outdated orders and compliance gaps.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-lg font-heading font-semibold">Select documents</h2>
            {docs.length === 0 ? (
              <div className="bg-white border border-[#E8E6E1] rounded-lg p-8 text-center">
                <FileText className="w-7 h-7 text-[#C9CFC9] mx-auto" strokeWidth={1.5} />
                <p className="mt-3 text-sm text-[#57605A]">No analyzed documents yet. Upload documents or load demo data from your dashboard first.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {docs.map((d) => (
                  <label
                    key={d.id}
                    data-testid={`careplan-doc-${d.id}`}
                    className={`flex items-center gap-3 bg-white border rounded-lg p-3.5 cursor-pointer transition-all duration-200 ${
                      selected.includes(d.id) ? "border-[#2E5C50] ring-1 ring-[#2E5C50] bg-[#D6E0D9]/10" : "border-[#E8E6E1] hover:border-[#2E5C50]/50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(d.id)}
                      onChange={() => toggle(d.id)}
                      className="accent-[#2E5C50] w-4 h-4"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1C211F] truncate">{d.file_name}</p>
                      <p className="text-xs text-[#57605A]">{d.client_name || d.uploader_name}</p>
                    </div>
                    <DocTypeBadge type={d.document_type} />
                  </label>
                ))}
              </div>
            )}
            <button
              data-testid="run-careplan-btn"
              onClick={run}
              disabled={running || selected.length === 0}
              className="w-full flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-6 py-3 font-medium disabled:opacity-50"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
              {running ? "Analyzing — about 20–30 seconds…" : `Analyze ${selected.length || ""} document${selected.length === 1 ? "" : "s"}`}
            </button>
          </div>

          <div className="lg:col-span-3 space-y-6">
            <h2 className="text-lg font-heading font-semibold">Analysis report</h2>
            {!r ? (
              <div className="bg-white border border-[#E8E6E1] rounded-xl p-10 text-center">
                <ClipboardList className="w-8 h-8 text-[#C9CFC9] mx-auto" strokeWidth={1.5} />
                <p className="mt-3 text-sm text-[#57605A]">No report yet — select documents and run an analysis.</p>
              </div>
            ) : (
              <div data-testid="careplan-report" className="bg-white border border-[#E8E6E1] rounded-xl p-6 md:p-8 space-y-6 fade-in-up">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-[#57605A]">
                    {new Date(current.created_at).toLocaleString()} · {current.document_names?.join(", ")}
                  </p>
                  <RiskBadge level={r.overall_risk} />
                </div>

                <div>
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">Plan summary</h3>
                  <p className="mt-2 leading-relaxed text-[#1C211F]">{r.plan_summary}</p>
                </div>

                <div>
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">Issues found</h3>
                  {r.issues_found?.length > 0 ? (
                    <ul className="mt-3 space-y-2">
                      {r.issues_found.map((iss, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                          <AlertTriangle className="w-4 h-4 text-[#C48231] shrink-0 mt-0.5" strokeWidth={1.5} />
                          {iss}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-[#34745C] flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> No issues found.
                    </p>
                  )}
                </div>

                <div>
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">Compliance risks</h3>
                  {r.compliance_risks?.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {r.compliance_risks.map((c, i) => (
                        <div key={i} className={`flex items-start gap-3 border rounded-lg p-3.5 ${SEVERITY_CLS[c.severity] || SEVERITY_CLS.medium}`}>
                          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider">{c.severity}</p>
                            <p className="text-sm text-[#1C211F] mt-0.5">{c.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-[#34745C]">No compliance risks identified.</p>
                  )}
                </div>

                <div>
                  <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">
                    <ListChecks className="w-4 h-4" strokeWidth={1.5} /> Recommended updates
                  </h3>
                  <ol className="mt-3 space-y-2">
                    {(r.recommended_updates || []).map((u, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm leading-relaxed">
                        <span className="w-5 h-5 bg-[#2E5C50] text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                        {u}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}

            {reports.length > 1 && (
              <div>
                <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A] mb-3">Previous reports</h3>
                <div className="space-y-2">
                  {reports.map((rep) => (
                    <button
                      key={rep.id}
                      data-testid={`careplan-history-${rep.id}`}
                      onClick={() => setCurrent(rep)}
                      className={`w-full text-left bg-white border rounded-lg px-4 py-3 flex items-center justify-between gap-3 transition-colors duration-150 ${
                        current?.id === rep.id ? "border-[#2E5C50]" : "border-[#E8E6E1] hover:border-[#2E5C50]/50"
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#1C211F] truncate">{rep.document_names?.join(", ")}</p>
                        <p className="text-xs text-[#57605A]">
                          {new Date(rep.created_at).toLocaleString()} · by {rep.requested_by_name}
                        </p>
                      </div>
                      <RiskBadge level={rep.report?.overall_risk} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
